import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function PartnerForm() {
  const [formData, setFormData] = useState({
    companyName: "",
    contactPerson: "",
    productsOffered: "",
    email: "",
    phone: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Company name
    if (!formData.companyName.trim()) {
      newErrors.companyName = "Please enter company name";
    }

    // Contact person
    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = "Please enter contact person full name";
    }

    // Products offered
    if (!formData.productsOffered.trim()) {
      newErrors.productsOffered = "Please specify products offered";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Please enter email address";
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = "Enter a valid email address";
    }

    // Phone validation (10-digit mobile)
    const phoneClean = formData.phone.trim().replace(/\D/g, "");
    if (!formData.phone.trim()) {
      newErrors.phone = "Please enter mobile number";
    } else if (!/^[6-9]\d{9}$/.test(phoneClean)) {
      newErrors.phone = "Enter a valid 10-digit mobile number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields properly.", {
        position: "bottom-right",
        theme: "dark",
        autoClose: 3500,
      });
      return;
    }

    setIsSubmitting(true);

    setTimeout(() => {
      setIsSubmitting(false);
      toast.success(
        `Thank you ${formData.contactPerson.trim()}! We reply within 1 working day with an intake configuration draft.`,
        {
          position: "bottom-right",
          theme: "dark",
          autoClose: 5000,
        }
      );

      // Reset form
      setFormData({
        companyName: "",
        contactPerson: "",
        productsOffered: "",
        email: "",
        phone: "",
      });
      setErrors({});
    }, 600);
  };

  return (
    <section id="partner-as-lender" className="overflow-hidden bg-[#f8faf9] section-spacing">
      <div className="section-container">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14 xl:gap-16">
          {/* ================= LEFT CONTENT ================= */}
          <div className="lg:col-span-5">
            {/* Label */}
            <p className="font-jakarta text-[11px] font-medium tracking-[0.34em] text-[var(--mint-green)] sm:text-[12px]">
              PARTNER AS A LENDER
            </p>

            {/* Heading */}
            <h2 className="mt-4 font-bricolage text-[36px] font-semibold leading-[1.08] tracking-[-1px] text-[#0b1720] sm:mt-5 sm:text-[44px] md:text-[48px] lg:text-[52px]">
              Tell us what paper you want.
            </h2>

            {/* Description */}
            <p className="mt-4 font-jakarta text-[15px] leading-[1.6] text-[#556369] sm:mt-5 sm:text-[16px]">
              We reply within one working day with an intake configuration draft.
            </p>
          </div>

          {/* ================= RIGHT FORM CARD ================= */}
          <div className="lg:col-span-7">
            <div className="relative overflow-hidden rounded-[24px] border border-[#e2e9e5] bg-white p-6 shadow-[0_20px_50px_rgba(0,0,0,0.04)] sm:rounded-[28px] sm:p-8 md:p-10">
              <form onSubmit={handleSubmit} noValidate>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
                  {/* Company Name */}
                  <div>
                    <label
                      htmlFor="companyName"
                      className="block font-jakarta text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7d8b85] sm:text-[11px]"
                    >
                      COMPANY NAME
                    </label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="NBFC or bank"
                      className={`mt-2 w-full rounded-xl border bg-[#fbfdfc] px-4 py-3.5 font-jakarta text-[14px] text-[#0b1720] placeholder:text-[#99a6a0] sm:text-[15px] focus:outline-none transition-colors ${
                        errors.companyName
                          ? "border-red-500 focus:border-red-500"
                          : "border-[#e2e9e5] focus:border-[var(--mint-green)]"
                      }`}
                    />
                    {errors.companyName && (
                      <p className="mt-1.5 font-jakarta text-[12px] text-red-500">
                        {errors.companyName}
                      </p>
                    )}
                  </div>

                  {/* Contact Person */}
                  <div>
                    <label
                      htmlFor="contactPerson"
                      className="block font-jakarta text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7d8b85] sm:text-[11px]"
                    >
                      CONTACT PERSON
                    </label>
                    <input
                      type="text"
                      id="contactPerson"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleChange}
                      placeholder="Full name"
                      className={`mt-2 w-full rounded-xl border bg-[#fbfdfc] px-4 py-3.5 font-jakarta text-[14px] text-[#0b1720] placeholder:text-[#99a6a0] sm:text-[15px] focus:outline-none transition-colors ${
                        errors.contactPerson
                          ? "border-red-500 focus:border-red-500"
                          : "border-[#e2e9e5] focus:border-[var(--mint-green)]"
                      }`}
                    />
                    {errors.contactPerson && (
                      <p className="mt-1.5 font-jakarta text-[12px] text-red-500">
                        {errors.contactPerson}
                      </p>
                    )}
                  </div>

                  {/* Products Offered */}
                  <div>
                    <label
                      htmlFor="productsOffered"
                      className="block font-jakarta text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7d8b85] sm:text-[11px]"
                    >
                      PRODUCTS OFFERED
                    </label>
                    <input
                      type="text"
                      id="productsOffered"
                      name="productsOffered"
                      value={formData.productsOffered}
                      onChange={handleChange}
                      placeholder="e.g. Business, LAP"
                      className={`mt-2 w-full rounded-xl border bg-[#fbfdfc] px-4 py-3.5 font-jakarta text-[14px] text-[#0b1720] placeholder:text-[#99a6a0] sm:text-[15px] focus:outline-none transition-colors ${
                        errors.productsOffered
                          ? "border-red-500 focus:border-red-500"
                          : "border-[#e2e9e5] focus:border-[var(--mint-green)]"
                      }`}
                    />
                    {errors.productsOffered && (
                      <p className="mt-1.5 font-jakarta text-[12px] text-red-500">
                        {errors.productsOffered}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block font-jakarta text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7d8b85] sm:text-[11px]"
                    >
                      EMAIL
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="name@company.com"
                      className={`mt-2 w-full rounded-xl border bg-[#fbfdfc] px-4 py-3.5 font-jakarta text-[14px] text-[#0b1720] placeholder:text-[#99a6a0] sm:text-[15px] focus:outline-none transition-colors ${
                        errors.email
                          ? "border-red-500 focus:border-red-500"
                          : "border-[#e2e9e5] focus:border-[var(--mint-green)]"
                      }`}
                    />
                    {errors.email && (
                      <p className="mt-1.5 font-jakarta text-[12px] text-red-500">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label
                      htmlFor="phone"
                      className="block font-jakarta text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7d8b85] sm:text-[11px]"
                    >
                      PHONE
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="10–digit mobile"
                      maxLength={10}
                      className={`mt-2 w-full rounded-xl border bg-[#fbfdfc] px-4 py-3.5 font-jakarta text-[14px] text-[#0b1720] placeholder:text-[#99a6a0] sm:text-[15px] focus:outline-none transition-colors ${
                        errors.phone
                          ? "border-red-500 focus:border-red-500"
                          : "border-[#e2e9e5] focus:border-[var(--mint-green)]"
                      }`}
                    />
                    {errors.phone && (
                      <p className="mt-1.5 font-jakarta text-[12px] text-red-500">
                        {errors.phone}
                      </p>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="mt-6 sm:mt-8">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex h-[50px] items-center justify-center rounded-full bg-[var(--mint-green)] px-8 font-jakarta text-[15px] font-semibold text-[#071b17] shadow-lg shadow-[var(--mint-green)]/20 transition-all duration-200 hover:brightness-105 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 cursor-pointer sm:h-[54px] sm:px-9"
                  >
                    {isSubmitting ? "Submitting..." : "Partner as a lender"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* React Toast Container */}
      <ToastContainer />
    </section>
  );
}

export default PartnerForm;
